import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label, StatusChip, cad, shortId } from "@/components/sponsored/format";
import { LedgerRows } from "@/components/sponsored/LedgerTable";

function Detail({ storeId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/sponsored-cashback/merchants/${storeId}`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [storeId]);
  const f = d?.overview?.funds;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="admin-sc-detail-dialog">
        <DialogHeader>
          <DialogTitle>{d?.store?.name || "Merchant"}</DialogTitle>
          <DialogDescription>Sponsored Cashback budget, repeat purchases and ledger for this merchant.</DialogDescription>
        </DialogHeader>
        {!d ? <p className="text-sm text-slate-500" data-testid="admin-sc-detail-loading">Loading…</p> : d.error ? <p className="text-sm text-rose-600" data-testid="admin-sc-detail-error">Couldn't load this merchant.</p> : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[["Funded", f.funded_cad], ["Available", f.available_cad], ["Set aside", f.reserved_cad], ["Pending", f.pending_cad],
                ["Paid to shoppers", f.settled_cad], ["Refund hold", f.refund_hold_cad], ["Refunded", f.refunded_cad]].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-slate-200 p-3"><Label>{k}</Label><div className="tabular-nums font-semibold mt-1">{cad(v)}</div></div>
              ))}
            </div>
            <div data-testid="admin-sc-detail-issues">
              <Label>Reconciliation check</Label>
              {d.issues.length === 0 ? <p className="text-sm text-emerald-700 mt-1">Budget, ledger and reward records match.</p>
                : <ul className="text-sm text-rose-700 list-disc pl-4 mt-1">{d.issues.map((i) => <li key={i}>{i}</li>)}</ul>}
            </div>
            <div><Label>Repeat purchases ({d.transactions.total})</Label>
              <ul className="text-sm divide-y divide-slate-100 mt-1">{d.transactions.rows.slice(0, 20).map((r) => (
                <li key={r.sponsored_reward_id} className="py-1.5 flex flex-wrap justify-between gap-2" data-testid={`admin-sc-detail-tx-${r.sponsored_reward_id}`}><span className="font-mono text-xs" title={`${r.sponsored_reward_id} · ${r.merchant_order_id || "—"}`}>{shortId(r.sponsored_reward_id, 12)} · {r.merchant_order_id || "—"} · {r.shopper_ref}</span><span className="inline-flex items-center gap-2 tabular-nums">{cad(r.merchant_sponsored_cad)} <StatusChip status={r.state} /></span></li>
              ))}</ul></div>
            <div><Label>Budget ledger</Label><div className="mt-1"><LedgerRows rows={d.ledger.slice(0, 50)} testid="admin-sc-ledger" /></div></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AdminScMerchants({ rows }) {
  const [open, setOpen] = useState(null);
  if (!rows.length) return <p className="text-sm text-slate-400 italic" data-testid="admin-sc-merchants-empty">No merchant has set up Sponsored Cashback yet.</p>;
  return (
    <div className="relative overflow-x-auto" data-testid="admin-sc-merchants">
      <table className="w-full text-sm">
        <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500">
          <tr>{["Merchant", "Offer", "Available", "Set aside", "Pending", "Paid", "Repeat purchases", "Reversed", "Check", ""].map((h) => <th key={h || "actions"} scope="col" className="pb-2 pr-3 whitespace-nowrap">{h || <span className="sr-only">Actions</span>}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.store_id} className="border-t border-slate-100" data-testid={`admin-sc-merchant-${r.store_id}`}>
              <td className="py-2 pr-3 font-semibold">{r.name}{!r.plan_eligible && <span className="ml-2 text-[11px] text-amber-700">plan not eligible</span>}</td>
              <td className="py-2 pr-3">{r.campaign.enabled ? `On · ${r.campaign.total_rate_pct}%` : "Off"}</td>
              <td className="py-2 pr-3 tabular-nums">{cad(r.funds.available_cad)}</td>
              <td className="py-2 pr-3 tabular-nums">{cad(r.funds.reserved_cad)}</td>
              <td className="py-2 pr-3 tabular-nums">{cad(r.funds.pending_cad)}</td>
              <td className="py-2 pr-3 tabular-nums">{cad(r.funds.settled_cad)}</td>
              <td className="py-2 pr-3">{r.kpis.funnel.repeat_purchases}</td>
              <td className="py-2 pr-3">{r.kpis.rewards.REVERSED.count}</td>
              <td className={`py-2 pr-3 text-xs font-semibold ${r.issues.length ? "text-rose-700" : "text-emerald-700"}`} data-testid={`admin-sc-issues-${r.store_id}`}>{r.issues.length ? `${r.issues.length} issue(s)` : "OK"}</td>
              <td className="py-2 pr-3"><button onClick={() => setOpen(r.store_id)} className="text-xs font-semibold text-[#004C3F] hover:underline" data-testid={`admin-sc-detail-${r.store_id}`}>Details</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {open && <Detail storeId={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
